import type { AppointmentType } from '@/features/appointments/utils/AppointmentType';
import type { Branch } from '@/features/appointments/utils/Branch';
import type { ConfirmedAppointmentData } from '@/features/appointments/utils/ConfirmedAgentInfo';
import type { GetAppointmentInfoResponseData } from '../api/getAppointmentInformation';

export type AgentInfo = ConfirmedAppointmentData['agentInfo'];

export type AppointmentHydrationPayload = {
  appointmentType: AppointmentType;
  branch?: Branch;
  confirmedAppointment: ConfirmedAppointmentData;
  selectedDestination: {
    destinationApiName: string;
    workTypeId: string;
    name: string;
  };
  searchedDestination?: string;
  selectedAgent: AgentInfo;
  appointmentId: string;
  contactInfo: { email: string };
  selectedStartTime: string;
  selectedEndTime: string;
  confirmationId: string;
  customerId: string;
};

export type AppointmentHydrationActions = {
  setSelectedBranch: (branch: Branch | null) => void;
  setConfirmedAppointment: (appointment: ConfirmedAppointmentData) => void;
  setSelectedDestination: (destination: {
    destinationApiName: string;
    workTypeId: string;
    name: string;
  }) => void;
  setSearchedDestination: (destination: string) => void;
  setSelectedAgent: (agent: AgentInfo) => void;
  setAppointmentId: (appointmentId: string) => void;
  setSelectedAppointmentType: (appointmentType: AppointmentType) => void;
  setContactInfo: (contactInfo: { email: string }) => void;
  setSelectedStartTime: (startTime: string) => void;
  setSelectedEndTime: (endTime: string) => void;
  setConfirmationId: (confirmationId: string) => void;
  setCustomerId: (customerId: string) => void;
  setSessionSource: (sessionSource: 'email') => void;
};

export const resolveAppointmentType = (
  appointmentType: GetAppointmentInfoResponseData['appointmentType'],
): AppointmentType =>
  appointmentType === 'In Person' ? 'in-branch' : 'online';

export const isCanceledAppointment = (status?: string): boolean =>
  status === 'Canceled';

export const shouldHydrateAppointment = (
  hydratedAppointmentId: string | null,
  appointmentId: string,
): boolean => hydratedAppointmentId !== appointmentId;

export const buildAgentInfo = (
  data: GetAppointmentInfoResponseData,
): AgentInfo => ({
  agentRecordId: data.agentUserId,
  resourceId: data.appointmentServiceResourceId,
  serviceTerritoryId: data.serviceTerritoryId,
  name: data.name,
  image: data.image,
});

export const buildSelectedBranch = (
  data: GetAppointmentInfoResponseData,
): Branch => ({
  branchId: data.serviceTerritoryId,
  branchName: data.serviceTerritoryName || '',
  branchAddress: '',
  distance: 0,
  city: '',
  latitude: 0,
  longitude: 0,
  postalCode: '',
  state: '',
  street: '',
});

export const buildAppointmentHydrationPayload = (
  data: GetAppointmentInfoResponseData,
): AppointmentHydrationPayload => {
  const appointmentType = resolveAppointmentType(data.appointmentType);
  const agentInfo = buildAgentInfo(data);

  return {
    appointmentType,
    ...(appointmentType === 'in-branch'
      ? { branch: buildSelectedBranch(data) }
      : {}),
    confirmedAppointment: {
      agentInfo,
      selectedDate: new Date(data.schedStartTime),
      selectedStartTime: data.schedStartTime,
      selectedEndTime: data.schedEndTime,
    },
    selectedDestination: {
      destinationApiName: data.destination,
      workTypeId: data.workTypeId,
      name: data.destination,
    },
    ...(data.searchedDestination
      ? { searchedDestination: data.searchedDestination }
      : {}),
    selectedAgent: agentInfo,
    appointmentId: data.appointmentId,
    contactInfo: { email: data.email },
    selectedStartTime: data.schedStartTime,
    selectedEndTime: data.schedEndTime,
    confirmationId: data.appointmentId,
    customerId: data.customerId,
  };
};

export const applyAppointmentHydration = (
  payload: AppointmentHydrationPayload,
  actions: AppointmentHydrationActions,
): void => {
  if (payload.branch) {
    actions.setSelectedBranch(payload.branch);
  }

  actions.setConfirmedAppointment(payload.confirmedAppointment);
  actions.setSelectedDestination(payload.selectedDestination);

  if (payload.searchedDestination) {
    actions.setSearchedDestination(payload.searchedDestination);
  }

  actions.setSelectedAgent(payload.selectedAgent);
  actions.setAppointmentId(payload.appointmentId);
  actions.setSelectedAppointmentType(payload.appointmentType);
  actions.setContactInfo(payload.contactInfo);
  actions.setSelectedStartTime(payload.selectedStartTime);
  actions.setSelectedEndTime(payload.selectedEndTime);
  actions.setConfirmationId(payload.confirmationId);
  actions.setCustomerId(payload.customerId);
  actions.setSessionSource('email');
};
